import { useMutation } from '@tanstack/react-query';
import { rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type {
  StoreSecretInput,
  DeleteSecretInput,
  GetSmsNumberInput,
  RegisterSmsNumberInput,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

type TestGenerateInput = Parameters<typeof rpcClient.otp.testGenerate>[0];

export function useStoreOTPSecret(options?: { onSuccess?: () => void }) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.otp.listTargets.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: StoreSecretInput) => rpcClient.otp.storeSecret(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'OTP secret stored successfully',
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to store OTP secret',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

export function useTestOTPGenerate() {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: TestGenerateInput) => rpcClient.otp.testGenerate(input),
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to generate OTP',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

export function useRegisterSmsNumber(options?: { onSuccess?: () => void }) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.otp.getSmsNumber.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: RegisterSmsNumberInput) =>
      rpcClient.otp.registerSmsNumber(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'SMS number saved',
        color: 'success',
        icon: 'check',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to save SMS number',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

export function useDeleteSmsNumber(options?: { onSuccess?: () => void }) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.otp.getSmsNumber.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: GetSmsNumberInput) =>
      rpcClient.otp.deleteSmsNumber(input),
    onSuccess: (data) => {
      if (data.success) {
        showToast({
          title: 'Success',
          message: 'SMS number removed',
          color: 'success',
          icon: 'check',
        });
        invalidateRpcQueryFn({});
        options?.onSuccess?.();
      } else {
        showToast({
          title: 'Error',
          message: data.message || 'Failed to remove SMS number',
          color: 'error',
          icon: 'x',
        });
      }
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to remove SMS number',
        color: 'error',
        icon: 'x',
      });
    },
  });
}

export function useDeleteOTPSecret(options?: { onSuccess?: () => void }) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.otp.listTargets.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: DeleteSecretInput) => rpcClient.otp.deleteSecret(input),
    onSuccess: (data) => {
      if (data.success) {
        showToast({
          title: 'Success',
          message: 'OTP secret deleted successfully',
          color: 'success',
          icon: 'check',
        });
        invalidateRpcQueryFn({});
        options?.onSuccess?.();
      } else {
        showToast({
          title: 'Error',
          message: data.message || 'Failed to delete OTP secret',
          color: 'error',
          icon: 'x',
        });
      }
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to delete OTP secret',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
